import React, { useState, useEffect } from 'react';

/**
 * Dismissible welcome banner shown ONCE to first-time visitors above the shop.
 * Dismissal is persisted in localStorage so the banner never comes back for
 * that device. Admins can clear the flag via DevTools if they need to retest
 * (`localStorage.removeItem('loop_welcome_dismissed')`).
 */
const DISMISS_KEY = 'loop_welcome_dismissed';

const WelcomeBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY) === 'true';
      if (!dismissed) setVisible(true);
    } catch (e) {
      // Private-browsing / blocked storage — fall back to always-show behaviour
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, 'true'); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  const copyCode = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText('LOOPER10');
      }
    } catch (e) { 
      // Silently ignore clipboard errors (permissions, HTTPS requirement, etc.)
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="welcome-discount-banner"
      role="status"
      aria-live="polite"
      style={{
        background: 'linear-gradient(90deg, #1E4B32 0%, #2C6E49 50%, #1E4B32 100%)',
        color: '#FEFEE3',
        padding: '10px 14px',
        borderBottom: '3px solid #000',
        boxShadow: '0 2px 0 0 #FFC857 inset',
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '12px',
        letterSpacing: '0.1em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        position: 'relative',
      }}
    >
      <span style={{ color: '#FFC857', fontSize: '14px' }} aria-hidden="true">⛳</span>
      <span>
        <strong style={{ color: '#FEFEE3' }}>WELCOME TO THE LOOP</strong>
        <span style={{ color: '#CFCFB8', margin: '0 8px' }}>—</span>
        <span>10% OFF WITH CODE&nbsp;</span>
        <button
          onClick={copyCode}
          data-testid="welcome-copy-code-btn"
          title="Click to copy code"
          style={{
            background: '#FFC857',
            color: '#000',
            padding: '3px 10px',
            fontSize: '12px',
            border: '2px solid #000',
            fontWeight: 'bold',
            letterSpacing: '0.12em',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          LOOPER10
        </button>
      </span>
      <button
        onClick={dismiss}
        data-testid="welcome-banner-dismiss"
        aria-label="Dismiss welcome banner"
        style={{
          background: 'transparent',
          color: '#FEFEE3',
          border: '2px solid #FEFEE3',
          padding: '2px 10px',
          fontSize: '11px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontWeight: 'bold',
          marginLeft: '8px',
        }}
      >
        ✕
      </button>
    </div>
  );
};

export default WelcomeBanner;
